import React, { useState, useEffect } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { createProject, saveProjectToWeb } from '../firebase/workspaceService';
import { GRID_SIZES, DEFAULT_GRID_SIZE_CODE } from '../store/houseSlice';
import { hasGuestDesign, loadGuestDesign, clearGuestDesign } from '../utils/guestDesign';
import UserMenu from '../components/UserMenu';
import NavBrand from '../components/NavBrand';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import RecommendationsFeed from '../components/RecommendationsFeed';
import DashboardBanner from '../components/DashboardBanner';
import PlanStrip from '../components/PlanStrip';
import FloorPlanImport from '../components/FloorPlanImport/FloorPlanImport';
import { floorPlanToDesign } from '../utils/floorPlanToRooms';
import './Dashboard.css';

const SIZE_OPTIONS = ['S', 'M', 'L'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate          = useNavigate();
  const { t } = useT();

  const [showModal,   setShowModal]   = useState(false);  // create-project modal
  const [projectName, setProjectName] = useState('');
  const [projectSize, setProjectSize] = useState(DEFAULT_GRID_SIZE_CODE);
  const [creating,    setCreating]    = useState(false);
  const [nameError,   setNameError]   = useState('');
  // An optional 2D floor plan: { file, detection, scaleFactor } from
  // FloorPlanImport, converted into rooms when the project is created.
  const [plan,        setPlan]        = useState(null);

  // A design started on the unauthenticated "/try" flow and handed off at
  // registration (see GuestSaveButton) — as soon as we land here with an
  // account, ask for a project name and adopt it instead of losing it.
  useEffect(() => {
    if (hasGuestDesign()) setShowModal(true);
  }, []);

  async function handleCreateAndOpen() {
    const name = projectName.trim();
    if (!name) { setNameError(t('ws_field_required')); return; }
    setCreating(true);
    try {
      const id = await createProject(user.uid, name, null, projectSize);
      const guestDesign = loadGuestDesign();
      if (guestDesign) {
        await saveProjectToWeb(id, JSON.stringify(guestDesign));
        clearGuestDesign();
      } else if (plan?.detection) {
        // An uploaded plan becomes ordinary rooms in the saved project, so it
        // opens in the Builder fully editable. A conversion that yields nothing
        // usable is not fatal — the project is simply created empty.
        try {
          const built = floorPlanToDesign(plan.detection, {
            scaleFactor: plan.scaleFactor,
            gridSize: GRID_SIZES[projectSize],
            imageWalls: plan.imageWalls,
          });
          if (built) await saveProjectToWeb(id, JSON.stringify(built.design));
        } catch (err) {
          console.error('Floor plan import failed:', err);
        }
      }
      setShowModal(false);
      setProjectName('');
      setProjectSize(DEFAULT_GRID_SIZE_CODE);
      setPlan(null);
      navigate(`/app/${id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
      setCreating(false);
    }
  }

  function handleModalKeyDown(e) {
    if (e.key === 'Enter') handleCreateAndOpen();
    if (e.key === 'Escape') { setShowModal(false); setProjectName(''); setNameError(''); setPlan(null); }
  }

  return (
    <div className="dash-root">

      {/* ── TOP NAV ── */}
      <header className="dash-nav">
        <NavBrand />

        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          <UserMenu />
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      {/* THE HOME TAB IS THE FEED. Both account types, one branch.
          A designer used to see their own identity strip and their own gallery
          above it — their profile, on the page meant for everyone else's work,
          pushing it below the fold. Both live on /profile now, which is reached
          the same way from either account: the tab bar on a phone, the name and
          picture in the header on a desktop. A customer's three get-started
          cards went the same way, for the same reason. */}
      <main className="dash-main dash-main--wide">
        {/* Above the feed rather than inside it: the feed is a list of other
            people's work and this is not part of that list. */}
        {/* Designers only; renders nothing for anyone else. */}
        <PlanStrip />
        <DashboardBanner />
        <RecommendationsFeed />
      </main>

      {/* ── CREATE PROJECT MODAL ── */}
      {showModal && (
        <div className="dash-modal-overlay" onClick={() => { setShowModal(false); setProjectName(''); setProjectSize(DEFAULT_GRID_SIZE_CODE); setNameError(''); setPlan(null); }}>
          <div className="dash-modal" onClick={e => e.stopPropagation()} onKeyDown={handleModalKeyDown}>
            <h3 className="dash-modal-title">{t('ws_modal_project_title')}</h3>
            <p className="dash-modal-sub">{t('dash_new_project_sub')}</p>
            <input
              className={`dash-modal-input${nameError ? ' dash-modal-input--error' : ''}`}
              placeholder={t('ws_modal_project_ph')}
              value={projectName}
              onChange={e => { setProjectName(e.target.value); setNameError(''); }}
              autoFocus
            />
            {nameError && <p className="dash-modal-error">{nameError}</p>}

            <p className="dash-modal-size-label">{t('ws_size_label')}</p>
            <div className="dash-modal-size-row">
              {SIZE_OPTIONS.map(code => (
                <button
                  key={code}
                  type="button"
                  className={`dash-modal-size-btn${projectSize === code ? ' dash-modal-size-btn--active' : ''}`}
                  onClick={() => setProjectSize(code)}
                >
                  <span className="dash-modal-size-code">{code}</span>
                  <span className="dash-modal-size-dims">{GRID_SIZES[code]}×{GRID_SIZES[code]}</span>
                </button>
              ))}
            </div>

            <FloorPlanImport onChange={setPlan} disabled={creating} />

            <div className="dash-modal-actions">
              <button
                className="dash-modal-btn dash-modal-btn--primary"
                onClick={handleCreateAndOpen}
                disabled={creating}
              >
                {creating ? t('ws_creating') : t('ws_create')}
              </button>
              <button
                className="dash-modal-btn dash-modal-btn--ghost"
                onClick={() => { setShowModal(false); setProjectName(''); setProjectSize(DEFAULT_GRID_SIZE_CODE); setNameError(''); setPlan(null); }}
                disabled={creating}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
